import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Clock, Video, Loader2 } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { liveClassService } from '../../services/live-class.service';
import type { MyLiveClass } from '@edytech/shared/types';

type Scope = 'upcoming' | 'past' | 'recorded';

const SCOPE_TABS: { value: Scope; label: string }[] = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Past' },
  { value: 'recorded', label: 'Recorded' },
];

const statusLabel: Record<string, string> = {
  SCHEDULED: 'Scheduled',
  ONGOING: 'Live now',
  ENDED: 'Ended',
  CANCELLED: 'Cancelled',
};

export const MyLiveClasses: React.FC = () => {
  const [scope, setScope] = useState<Scope>('upcoming');

  const { data: classes, isLoading } = useQuery<MyLiveClass[]>({
    queryKey: ['my-live-classes', scope],
    queryFn: () => liveClassService.getMyLiveClasses(scope),
  });

  return (
    <div>
      <h1 className="text-2xl font-bold">My Live Classes</h1>
      <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">
        Your registrations and recordings.
      </p>

      <div className="mt-5 flex gap-2">
        {SCOPE_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setScope(tab.value)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              scope === tab.value
                ? 'bg-primary text-white'
                : 'bg-[rgb(var(--card))] text-[rgb(var(--light-secondary))] border border-[rgb(var(--border))]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        {isLoading && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {!isLoading && (!classes || classes.length === 0) && (
          <div className="rounded-xl border border-dashed border-[rgb(var(--border))] p-12 text-center">
            <Video className="mx-auto h-10 w-10 opacity-40" />
            <p className="mt-3 text-[rgb(var(--light-secondary))]">
              {scope === 'upcoming' && "You haven't registered for any upcoming live classes."}
              {scope === 'past' && 'No past live classes yet.'}
              {scope === 'recorded' && 'No recordings available yet.'}
            </p>
            <Link to="/live-classes" className="mt-4 inline-block text-sm font-medium text-primary hover:underline">
              Browse live classes →
            </Link>
          </div>
        )}

        {classes?.map((item) => (
          <Card key={item.registration.id} className="p-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <Link to={`/live-classes/${item.liveClass.id}`} className="font-semibold hover:text-primary">
                  {item.liveClass.title}
                </Link>
                <p className="text-sm text-[rgb(var(--light-secondary))]">
                  {item.session.title || item.liveClass.title}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-[rgb(var(--light-secondary))]">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {new Date(item.session.startsAt).toLocaleString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                    ({item.session.timezone})
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {statusLabel[item.session.status] ?? item.session.status}
                  </span>
                </div>
              </div>

              {item.session.recordingStatus === 'READY' && (
                <Link to={`/live-classes/${item.liveClass.id}`}>
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                    <Video className="h-3.5 w-3.5" /> Recording ready
                  </span>
                </Link>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
